import { useState } from "react";
import { twMerge } from "tailwind-merge";

import type { EditorTool, Point } from "./editorTools";
import type { EditorMark, NewMark } from "./marksHistory";

type Draft =
  | { current: Point; start: Point; type: "box" | "ellipse" }
  | { points: Point[]; type: "pencil" };

const PRIMARY_BUTTON = 0;
const STROKE_WIDTH = 1.5;
const FILL_OPACITY = 0.1;
const DASH = "6 4";

const clamp = (value: number) => Math.min(100, Math.max(0, value));

function pointFromEvent(
  event: { clientX: number; clientY: number },
  element: HTMLElement,
): Point {
  const rect = element.getBoundingClientRect();

  return [
    clamp(((event.clientX - rect.left) / rect.width) * 100),
    clamp(((event.clientY - rect.top) / rect.height) * 100),
  ];
}

function boxFrom(start: Point, end: Point) {
  return {
    height: Math.abs(end[1] - start[1]),
    width: Math.abs(end[0] - start[0]),
    x: Math.min(start[0], end[0]),
    y: Math.min(start[1], end[1]),
  };
}

function ellipseFrom({ height, width, x, y }: ReturnType<typeof boxFrom>) {
  return {
    cx: x + width / 2,
    cy: y + height / 2,
    rx: width / 2,
    ry: height / 2,
  };
}

function anchorOf(mark: EditorMark): Point {
  if (mark.type === "pencil") return mark.points[0]!;
  if (mark.type === "ellipse") return [mark.x + mark.w / 2, mark.y];

  return [mark.x, mark.y];
}

const shapeProps = {
  fill: "currentColor",
  fillOpacity: FILL_OPACITY,
  stroke: "currentColor",
  strokeDasharray: DASH,
  strokeWidth: STROKE_WIDTH,
  vectorEffect: "non-scaling-stroke" as const,
};

type AnnotationLayerProps = {
  isDisabled: boolean;
  marks: EditorMark[];
  onAddMark: (mark: NewMark) => void;
  tool: EditorTool;
};

export function AnnotationLayer({
  isDisabled,
  marks,
  onAddMark,
  tool,
}: AnnotationLayerProps) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [pendingText, setPendingText] = useState<null | Point>(null);

  const commitText = (text: string) => {
    const trimmed = text.trim();

    if (pendingText && trimmed) {
      onAddMark({
        size: 4,
        text: trimmed,
        type: "text",
        x: pendingText[0],
        y: pendingText[1],
      });
    }
    setPendingText(null);
  };

  const finishDraft = () => {
    if (draft?.type === "pencil" && draft.points.length > 1) {
      onAddMark({ points: draft.points, type: "pencil" });
    }
    if (draft?.type === "box" || draft?.type === "ellipse") {
      const { height, width, x, y } = boxFrom(draft.start, draft.current);

      if (width > 0.5 && height > 0.5) {
        onAddMark({ h: height, type: draft.type, w: width, x, y });
      }
    }
    setDraft(null);
  };

  return (
    <div
      className={twMerge(
        "absolute inset-0 touch-none [container-type:size]",
        !isDisabled && "cursor-crosshair",
      )}
      data-testid="annotation-layer"
      onClick={(event) => {
        if (isDisabled || tool !== "text") return;
        setPendingText(pointFromEvent(event, event.currentTarget));
      }}
      onPointerDown={(event) => {
        if (isDisabled || tool === "text") return;
        if (event.button !== PRIMARY_BUTTON) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        const point = pointFromEvent(event, event.currentTarget);

        setDraft(
          tool === "pencil"
            ? { points: [point], type: "pencil" }
            : { current: point, start: point, type: tool },
        );
      }}
      onPointerMove={(event) => {
        if (!draft) return;
        const point = pointFromEvent(event, event.currentTarget);

        setDraft(
          draft.type === "pencil"
            ? { ...draft, points: [...draft.points, point] }
            : { ...draft, current: point },
        );
      }}
      onPointerUp={finishDraft}
    >
      <svg
        className="pointer-events-none size-full text-text-brand-primary"
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
      >
        <g fill="none">
          {marks.map((mark) => (
            <MarkShape key={mark.n} mark={mark} />
          ))}
        </g>
        {draft?.type === "pencil" && (
          <polyline
            {...shapeProps}
            fill="none"
            points={draft.points.join(" ")}
            strokeDasharray={undefined}
          />
        )}
        {draft?.type === "box" && (
          <rect {...boxFrom(draft.start, draft.current)} {...shapeProps} />
        )}
        {draft?.type === "ellipse" && (
          <ellipse
            {...ellipseFrom(boxFrom(draft.start, draft.current))}
            {...shapeProps}
          />
        )}
      </svg>

      {marks.map((mark) => (
        <MarkOverlay key={mark.n} mark={mark} />
      ))}

      {pendingText && (
        <input
          aria-label="Text for the mark"
          autoFocus
          className="absolute min-w-32 rounded-md border border-border-brand bg-bg-primary px-2 py-1 text-sm text-text-primary outline-hidden"
          onBlur={(event) => commitText(event.currentTarget.value)}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === "Enter") commitText(event.currentTarget.value);
            if (event.key === "Escape") setPendingText(null);
            event.stopPropagation();
          }}
          style={{ left: `${pendingText[0]}%`, top: `${pendingText[1]}%` }}
        />
      )}
    </div>
  );
}

function MarkShape({ mark }: { mark: EditorMark }) {
  switch (mark.type) {
    case "box": {
      return (
        <rect
          {...shapeProps}
          height={mark.h}
          rx={0.8}
          width={mark.w}
          x={mark.x}
          y={mark.y}
        />
      );
    }
    case "ellipse": {
      return (
        <ellipse
          {...shapeProps}
          cx={mark.x + mark.w / 2}
          cy={mark.y + mark.h / 2}
          rx={mark.w / 2}
          ry={mark.h / 2}
        />
      );
    }
    case "pencil": {
      return (
        <polyline
          {...shapeProps}
          fill="none"
          points={mark.points.join(" ")}
          strokeDasharray={undefined}
        />
      );
    }
    case "text": {
      return null;
    }
  }
}

function MarkOverlay({ mark }: { mark: EditorMark }) {
  const [x, y] = anchorOf(mark);

  return (
    <>
      {mark.type === "text" && (
        <span
          className="pointer-events-none absolute whitespace-nowrap rounded-md bg-bg-primary/90 pl-5 pr-2 font-medium leading-[1.4] text-text-primary shadow-sm"
          style={{
            fontSize: `${mark.size}cqh`,
            left: `${mark.x}%`,
            top: `${mark.y}%`,
          }}
        >
          {mark.text}
        </span>
      )}
      <span
        className="pointer-events-none absolute flex size-[34px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-button-primary-bg text-sm font-extrabold text-button-primary-fg shadow-md"
        data-testid="mark-marker"
        style={{ left: `${x}%`, top: `${y}%` }}
      >
        {mark.n}
      </span>
    </>
  );
}
